(() => {
  const doc = document;
  const root = doc.documentElement;
  const theme = window.MahfouzTheme || {};
  const routes = theme.routes || {};
  const localeRoot = routes.root || '/';
  const routeBase = localeRoot.endsWith('/') ? localeRoot : `${localeRoot}/`;
  const ajaxRoute = (path) => `${routeBase}${String(path).replace(/^\/+/, '')}`;
  const cartJsonUrl = routes.cartAjax || ajaxRoute('cart.js');
  const cartAddAjaxUrl = routes.cartAddAjax || ajaxRoute('cart/add.js');
  const cartChangeAjaxUrl = routes.cartChangeAjax || ajaxRoute('cart/change.js');

  root.classList.remove('no-js');
  root.classList.add('js');

  const qs = (selector, scope = doc) => scope.querySelector(selector);
  const qsa = (selector, scope = doc) => Array.from(scope.querySelectorAll(selector));

  const setBodyLock = (locked) => {
    doc.body.classList.toggle('overflow-hidden', locked);
  };

  const moneyFromText = (text) => text || '';
  const formatMoney = (cents) => {
    const format = theme.moneyFormat || '${{amount}}';
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const amount = Math.max(0, Number(cents) || 0);
    const value = (amount / 100).toFixed(2);

    if (!placeholder) return value;

    return format.replace(placeholder[0], value);
  };

  const itemCountLabel = (count) => {
    const template = count === 1 ? theme.strings.cartItemCountOne : theme.strings.cartItemCountOther;
    return String(template || '').replace('__count__', count).replace('1', count);
  };

  const getDisplayedCartCount = () => {
    const countNode = qs('[data-cart-count]');
    return countNode ? Number(countNode.textContent || 0) : 0;
  };

  const getDisplayedCartTotal = () => {
    const subtotal = qs('[data-cart-subtotal]');
    return subtotal ? Number(subtotal.dataset.cartTotalPrice || 0) : 0;
  };

  const setCartCount = (count) => {
    const normalizedCount = Math.max(0, Number(count) || 0);
    qsa('[data-cart-count]').forEach((node) => {
      node.textContent = normalizedCount;
      node.setAttribute('aria-label', itemCountLabel(normalizedCount));
    });
    qsa('[data-cart-item-count-label]').forEach((node) => {
      node.textContent = itemCountLabel(normalizedCount);
    });
  };

  const escapeAttribute = (value = '') =>
    String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[char]);

  const escapeSelectorString = (value = '') =>
    String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

  const initHeader = () => {
    const header = qs('[data-site-header]');
    if (!header) return;

    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const togglePanel = (panel, open) => {
    if (!panel) return;
    panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    setBodyLock(open);
  };

  const initMobileMenu = () => {
    const menu = qs('[data-mobile-menu]');
    if (!menu) return;

    const closeMenu = () => {
      togglePanel(menu, false);
      qsa('details', menu).forEach((details) => {
        details.open = false;
      });
    };

    doc.addEventListener('click', (event) => {
      const openButton = event.target.closest('[data-mobile-menu-open]');
      const closeButton = event.target.closest('[data-mobile-menu-close]');
      if (openButton) togglePanel(menu, true);
      if (closeButton) closeMenu();
    });
  };

  const getCartDrawer = () => qs('[data-cart-drawer]');

  const openCartDrawer = () => togglePanel(getCartDrawer(), true);
  const closeCartDrawer = () => togglePanel(getCartDrawer(), false);

  const setCartDrawerLoading = (loading) => {
    const drawer = getCartDrawer();
    if (!drawer) return;
    drawer.classList.toggle('is-updating', loading);
    const panel = qs('.cart-drawer__panel', drawer);
    const status = qs('[data-cart-drawer-status]', drawer);
    if (panel) panel.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (status) status.setAttribute('aria-hidden', loading ? 'false' : 'true');
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      setCartCount(cart.item_count);
    } catch (error) {
      console.warn('Cart count update failed', error);
    }
  };

  const renderCartDrawer = (html) => {
    if (!html) return false;
    const current = qs('#shopify-section-cart-drawer');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = qs('#shopify-section-cart-drawer', parsed);
    if (!current || !next) return false;

    const wasOpen = getCartDrawer()?.getAttribute('aria-hidden') === 'false';
    current.replaceWith(next);
    if (wasOpen) togglePanel(getCartDrawer(), true);
    return true;
  };

  const refreshCartDrawer = async ({ updateCount = true } = {}) => {
    try {
      const url = `${routes.root}?sections=cart-drawer`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const sections = await response.json();
      renderCartDrawer(sections['cart-drawer']);
      if (updateCount) await updateCartCount();
    } catch (error) {
      console.warn('Cart drawer refresh failed', error);
    }
  };

  const refreshCartPage = async () => {
    try {
      const response = await fetch(routes.cart, { headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const currentPage = qs('.cart-page');
      const nextPage = qs('.cart-page', parsed);
      const currentSection = currentPage ? currentPage.closest('[id^="shopify-section-"]') : null;
      const nextSection = nextPage ? nextPage.closest('[id^="shopify-section-"]') : null;

      if (currentSection && nextSection) {
        currentSection.replaceWith(nextSection);
      } else if (currentPage && nextPage) {
        currentPage.replaceWith(nextPage);
      }

      await refreshCartDrawer();
    } catch (error) {
      console.warn('Cart page refresh failed', error);
    }
  };

  const updateQuantityControls = (quantityInput, quantity) => {
    if (!quantityInput) return;
    const normalizedQuantity = Math.max(0, Number(quantity) || 0);
    const input = qs('.quantity-input__input', quantityInput);
    if (input) input.value = normalizedQuantity;

    qsa('[data-cart-quantity][data-quantity-step]', quantityInput).forEach((button) => {
      const step = Number(button.dataset.quantityStep || 0);
      button.dataset.quantity = Math.max(0, normalizedQuantity + step);
    });
  };

  const updateCartProgress = (totalPrice) => {
    qsa('[data-cart-progress]').forEach((progress) => {
      const threshold = Number(progress.dataset.freeShippingThreshold || 0);
      if (!threshold) return;

      const remaining = threshold - totalPrice;
      const progressValue = Math.min(100, Math.max(0, Math.round((totalPrice * 100) / threshold)));
      const track = qs('[data-cart-progress-track]', progress);
      const bar = qs('[data-cart-progress-bar]', progress);
      const text = qs('[data-cart-progress-text]', progress);

      if (track) track.setAttribute('aria-valuenow', progressValue);
      if (bar) bar.style.width = `${progressValue}%`;
      if (!text) return;

      if (remaining > 0) {
        const remainingMessage = progress.dataset.freeShippingRemainingMessage || 'Add [amount] more for free delivery';
        text.textContent = remainingMessage.replace('[amount]', formatMoney(remaining));
      } else {
        text.textContent = progress.dataset.freeShippingSuccessMessage || 'Free delivery unlocked';
      }
    });
  };

  const updateCartDeliveryCharge = (totalPrice) => {
    const normalizedTotal = Math.max(0, Number(totalPrice) || 0);

    qsa('[data-cart-delivery-charge]').forEach((delivery) => {
      const threshold = Number(delivery.dataset.freeShippingThreshold || 0);
      const deliveryCharge = Number(delivery.dataset.deliveryChargeAmount || 0);
      const isFree = threshold > 0 && normalizedTotal >= threshold;
      const value = qs('[data-cart-delivery-charge-value]', delivery);
      const label = qs('[data-cart-delivery-charge-label]', delivery);
      const note = qs('[data-cart-delivery-charge-note]', delivery);

      delivery.classList.toggle('is-free', isFree);

      if (label) {
        label.textContent = !isFree && deliveryCharge > 0
          ? delivery.dataset.deliveryEstimateLabel || 'Estimated delivery'
          : delivery.dataset.deliveryLabel || 'Delivery';
      }

      if (value) {
        value.textContent = isFree
          ? delivery.dataset.deliveryFreeText || 'Free'
          : deliveryCharge > 0
            ? formatMoney(deliveryCharge)
            : delivery.dataset.deliveryCalculatedText || 'Calculated at checkout';
      }

      if (!note) return;

      if (isFree) {
        note.textContent = delivery.dataset.deliveryFreeNote || 'Free delivery applied.';
      } else if (threshold > 0) {
        const thresholdNote = deliveryCharge > 0
          ? delivery.dataset.deliveryEstimateNote || 'Delivery estimate may change at checkout by location.'
          : delivery.dataset.deliveryThresholdNote || 'Free from [amount]; otherwise calculated at checkout.';
        note.textContent = thresholdNote.replace('[amount]', formatMoney(threshold));
      } else {
        note.textContent = delivery.dataset.deliveryCheckoutNote || 'Final delivery options appear at checkout.';
      }
    });
  };

  const updateCartOrderTotal = (totalPrice) => {
    const normalizedTotal = Math.max(0, Number(totalPrice) || 0);

    qsa('[data-cart-totals]').forEach((totals) => {
      const threshold = Number(totals.dataset.freeShippingThreshold || 0);
      const deliveryCharge = Number(totals.dataset.deliveryChargeAmount || 0);
      const isFree = threshold > 0 && normalizedTotal >= threshold;
      const isCalculated = !isFree && deliveryCharge <= 0;
      const orderTotal = normalizedTotal + (isFree || isCalculated ? 0 : deliveryCharge);
      const value = qs('[data-cart-order-total]', totals);
      const label = qs('[data-cart-order-total-label]', totals);
      const note = qs('[data-cart-total-note]', totals);

      if (value) {
        value.dataset.cartOrderTotalPrice = orderTotal;
        value.textContent = formatMoney(orderTotal);
      }
      if (label) label.textContent = isCalculated ? totals.dataset.totalBeforeDeliveryLabel : totals.dataset.totalLabel;
      if (note) {
        note.textContent = isCalculated
          ? totals.dataset.totalNoteUnknown
          : isFree ? totals.dataset.totalNote : totals.dataset.totalNoteEstimated;
      }
    });
  };

  const setCartTotal = (totalPrice) => {
    const normalizedTotal = Math.max(0, Number(totalPrice) || 0);
    qsa('[data-cart-subtotal]').forEach((node) => {
      node.dataset.cartTotalPrice = normalizedTotal;
      node.textContent = formatMoney(normalizedTotal);
    });
    updateCartProgress(normalizedTotal);
    updateCartDeliveryCharge(normalizedTotal);
    updateCartOrderTotal(normalizedTotal);
  };

  const updateCartTotals = (cart) => {
    setCartCount(cart.item_count);
    setCartTotal(cart.total_price);
  };

  const updateCartLine = (cartItem, quantity, cartLine = null) => {
    if (!cartItem) return;
    const linePrice = qs('[data-cart-line-price]', cartItem);
    let finalLinePrice = null;

    if (cartLine) {
      cartItem.dataset.cartLineUnitPrice = cartLine.final_price;
      cartItem.dataset.cartLineFinalPrice = cartLine.final_line_price;
      finalLinePrice = cartLine.final_line_price;
    } else {
      const unitPrice = Number(cartItem.dataset.cartLineUnitPrice);
      if (Number.isFinite(unitPrice)) {
        finalLinePrice = unitPrice * Math.max(0, Number(quantity) || 0);
        cartItem.dataset.cartLineFinalPrice = finalLinePrice;
      }
    }

    if (linePrice && finalLinePrice !== null) linePrice.textContent = formatMoney(finalLinePrice);
  };

  const cartItemTargets = ({ key, line }) => {
    if (key) return qsa(`[data-cart-key="${escapeSelectorString(key)}"]`);
    if (line) return qsa(`[data-cart-line="${line}"]`);
    return [];
  };

  const cartPendingId = ({ key, line }) => key || `line:${line}`;

  const setCartLineRemoving = (identity, removing) => {
    cartItemTargets(identity).forEach((cartItem) => {
      cartItem.classList.toggle('is-removing', removing);
      cartItem.setAttribute('aria-hidden', removing ? 'true' : 'false');
    });
  };

  const updateCartLineSurfaces = (identity, quantity, cartLine = null) => {
    const normalizedQuantity = Math.max(0, Number(quantity) || 0);

    cartItemTargets(identity).forEach((cartItem) => {
      updateQuantityControls(qs('.quantity-input', cartItem), normalizedQuantity);
      updateCartLine(cartItem, normalizedQuantity, cartLine);
    });

    setCartLineRemoving(identity, normalizedQuantity === 0);
  };

  const pendingCartItems = new Map();
  const cartQuantityDelay = 220;
  let cartMutationQueue = Promise.resolve();
  let cartSurfaceRefreshTimer = null;
  let cartSurfaceRefreshPromise = Promise.resolve(true);
  let cartSurfaceRefreshPending = false;
  let resolveCartSurfaceRefresh = null;

  const cartWithPendingChanges = (cart) => {
    const items = Array.isArray(cart.items) ? cart.items.map((item) => ({ ...item })) : [];

    pendingCartItems.forEach((state) => {
      const quantity = Math.max(0, Number(state.quantity) || 0);
      const index = state.key
        ? items.findIndex((item) => item.key === state.key)
        : Number(state.line || 0) - 1;

      if (index < 0 || !items[index]) return;

      if (quantity === 0) {
        items.splice(index, 1);
        return;
      }

      const unitPrice = Number(
        items[index].final_price ?? items[index].price ?? state.cartItem?.dataset.cartLineUnitPrice ?? 0
      );
      const finalLinePrice = Math.max(0, Number.isFinite(unitPrice) ? unitPrice * quantity : 0);

      items[index] = {
        ...items[index],
        quantity,
        final_line_price: finalLinePrice,
        line_price: finalLinePrice
      };
    });

    return {
      ...cart,
      items,
      item_count: items.reduce((total, item) => total + Math.max(0, Number(item.quantity) || 0), 0),
      total_price: items.reduce((total, item) => {
        const linePrice = Number(item.final_line_price ?? item.line_price ?? 0);
        return total + (Number.isFinite(linePrice) ? linePrice : 0);
      }, 0)
    };
  };

  const refreshCartSurfaces = async () => {
    if (doc.body.classList.contains('template-cart')) {
      await refreshCartPage();
    } else {
      await refreshCartDrawer();
      openCartDrawer();
    }
  };

  const enqueueCartMutation = (task) => {
    const mutation = cartMutationQueue.catch(() => {}).then(task);
    cartMutationQueue = mutation.catch(() => {});
    return mutation;
  };

  const cancelCartSurfaceRefresh = () => {
    window.clearTimeout(cartSurfaceRefreshTimer);
    if (resolveCartSurfaceRefresh) resolveCartSurfaceRefresh(false);
    cartSurfaceRefreshPending = false;
    resolveCartSurfaceRefresh = null;
    cartSurfaceRefreshPromise = Promise.resolve(false);
  };

  const scheduleCartSurfaceRefresh = (delay = 140) => {
    cancelCartSurfaceRefresh();
    cartSurfaceRefreshPending = true;
    cartSurfaceRefreshPromise = new Promise((resolve) => {
      resolveCartSurfaceRefresh = resolve;
      cartSurfaceRefreshTimer = window.setTimeout(async () => {
        try {
          await refreshCartSurfaces();
          resolve(true);
        } catch (error) {
          console.warn('Cart surface refresh failed', error);
          resolve(false);
        } finally {
          cartSurfaceRefreshPending = false;
          resolveCartSurfaceRefresh = null;
        }
      }, delay);
    });
    return cartSurfaceRefreshPromise;
  };

  const setCheckoutLoading = (loading) => {
    qsa('[data-cart-checkout-button]').forEach((button) => {
      if (!button.dataset.originalText) button.dataset.originalText = button.textContent;
      button.disabled = loading;
      button.textContent = loading ? theme.strings.updatingCart || 'Updating cart' : button.dataset.originalText;
    });
  };

  const sendCartQuantityUpdate = async (pendingId) => {
    const state = pendingCartItems.get(pendingId);
    if (!state) return true;
    if (state.inFlight) return state.promise || true;

    window.clearTimeout(state.timer);
    state.inFlight = true;

    state.promise = enqueueCartMutation(async () => {
      let activeState = pendingCartItems.get(pendingId);

      while (activeState) {
        const sentQuantity = activeState.quantity;
        const body = activeState.key
          ? { id: activeState.key, quantity: sentQuantity }
          : { line: activeState.line, quantity: sentQuantity };

        const response = await fetch(cartChangeAjaxUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });

        if (!response.ok) throw new Error('Cart quantity update failed');

        const cart = await response.json();
        const latestState = pendingCartItems.get(pendingId);

        if (latestState && latestState.quantity !== sentQuantity) {
          latestState.inFlight = true;
          latestState.promise = state.promise;
          activeState = latestState;
          continue;
        }

        const cartLine = activeState.key
          ? cart.items.find((item) => item.key === activeState.key)
          : cart.items[activeState.line - 1];

        updateCartTotals(cartWithPendingChanges(cart));

        if (sentQuantity === 0 || !cartLine) {
          pendingCartItems.delete(pendingId);
          if (!pendingCartItems.size) scheduleCartSurfaceRefresh();
          return true;
        }

        updateCartLineSurfaces(activeState, sentQuantity, cartLine);
        pendingCartItems.delete(pendingId);
        return true;
      }

      return true;
    });

    try {
      return await state.promise;
    } catch (error) {
      console.warn('Cart quantity update failed', error);
      updateCartLineSurfaces(state, state.previousQuantity);
      setCartCount(state.previousCartCount);
      setCartTotal(state.previousCartTotal);
      pendingCartItems.delete(pendingId);
      return false;
    }
  };

  const flushPendingCartUpdates = async () => {
    if (!pendingCartItems.size && !cartSurfaceRefreshPending) return true;

    const pendingIds = Array.from(pendingCartItems.keys());
    pendingIds.forEach((pendingId) => {
      const state = pendingCartItems.get(pendingId);
      if (state) window.clearTimeout(state.timer);
    });

    const results = await Promise.all(pendingIds.map((pendingId) => sendCartQuantityUpdate(pendingId)));
    const synced = results.every((result) => result !== false);

    if (cartSurfaceRefreshPending) {
      await cartSurfaceRefreshPromise;
    }

    if (pendingCartItems.size) {
      return synced && flushPendingCartUpdates();
    }

    return synced;
  };

  const queueCartQuantityUpdate = ({ key, line, quantity, cartItem, quantityInput, previousQuantity, previousCartCount, previousCartTotal }) => {
    const pendingId = cartPendingId({ key, line });
    const currentState = pendingCartItems.get(pendingId) || {};

    window.clearTimeout(currentState.timer);
    pendingCartItems.set(pendingId, {
      ...currentState,
      key,
      line,
      cartItem,
      quantityInput,
      quantity,
      previousQuantity: currentState.previousQuantity ?? previousQuantity,
      previousCartCount: currentState.previousCartCount ?? previousCartCount,
      previousCartTotal: currentState.previousCartTotal ?? previousCartTotal,
      timer: window.setTimeout(() => sendCartQuantityUpdate(pendingId), quantity === 0 ? 0 : cartQuantityDelay)
    });
  };

  const initCartInteractions = () => {
    doc.addEventListener('click', async (event) => {
      const openButton = event.target.closest('[data-cart-open]');
      const closeButton = event.target.closest('[data-cart-close]');
      const quantityButton = event.target.closest('[data-cart-quantity]');
      const quantityStep = event.target.closest('[data-quantity-step]');

      if (openButton) {
        event.preventDefault();
        openCartDrawer();
      }

      if (closeButton) {
        event.preventDefault();
        closeCartDrawer();
      }

      if (quantityButton) {
        event.preventDefault();
        const line = Number(quantityButton.dataset.line);
        const key = quantityButton.dataset.cartKey || quantityButton.closest('.cart-item')?.dataset.cartKey || '';
        const quantity = Math.max(0, Number(quantityButton.dataset.quantity));
        if (!key && !line) return;

        const cartItem = quantityButton.closest('.cart-item');
        const quantityInput = quantityButton.closest('.quantity-input') || qs('.quantity-input', cartItem);
        const input = quantityInput ? qs('.quantity-input__input', quantityInput) : null;
        const previousQuantity = Math.max(0, Number(input ? input.value : quantity) || 0);
        const previousCartCount = getDisplayedCartCount();
        const previousCartTotal = getDisplayedCartTotal();
        const unitPrice = Number(cartItem ? cartItem.dataset.cartLineUnitPrice : 0);
        const nextCartCount = previousCartCount + quantity - previousQuantity;
        const nextCartTotal = previousCartTotal + ((quantity - previousQuantity) * (Number.isFinite(unitPrice) ? unitPrice : 0));
        const identity = { key, line };

        updateCartLineSurfaces(identity, quantity);
        setCartCount(nextCartCount);
        setCartTotal(nextCartTotal);
        queueCartQuantityUpdate({ key, line, quantity, cartItem, quantityInput, previousQuantity, previousCartCount, previousCartTotal });
      }

      if (quantityStep && !quantityStep.matches('[data-cart-quantity]')) {
        const quantity = quantityStep.closest('.quantity-input');
        const input = quantity ? qs('input', quantity) : null;
        if (!input) return;
        const step = Number(quantityStep.dataset.quantityStep);
        const min = Number(input.getAttribute('min') || 1);
        const nextValue = Math.max(min, Number(input.value || min) + step);
        input.value = nextValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    doc.addEventListener('submit', async (event) => {
      const checkoutForm = event.target.closest('[data-cart-checkout-form]');
      if (checkoutForm) {
        if (!pendingCartItems.size && !cartSurfaceRefreshPending) return;

        event.preventDefault();
        const submitter = event.submitter || qs('[data-cart-checkout-button]', checkoutForm);
        setCheckoutLoading(true);
        setCartDrawerLoading(true);

        const synced = await flushPendingCartUpdates();
        setCartDrawerLoading(false);

        if (synced) {
          if (getDisplayedCartCount() <= 0) {
            setCheckoutLoading(false);
            await refreshCartSurfaces();
            return;
          }

          const checkoutInput = doc.createElement('input');
          checkoutInput.type = 'hidden';
          checkoutInput.name = submitter && submitter.name ? submitter.name : 'checkout';
          checkoutInput.value = submitter && submitter.value ? submitter.value : '';
          checkoutForm.appendChild(checkoutInput);
          checkoutForm.submit();
          return;
        }

        setCheckoutLoading(false);
        await refreshCartSurfaces();
        return;
      }

      const form = event.target.closest('[data-ajax-cart-form]');
      if (!form) return;

      const submitter = event.submitter;
      if (submitter && submitter.classList.contains('shopify-payment-button__button')) return;

      event.preventDefault();
      const button = submitter || qs('[type="submit"]', form);
      const originalText = button ? button.textContent : '';
      let previousCartCount = getDisplayedCartCount();

      if (button) {
        button.disabled = true;
        button.textContent = theme.strings.adding || 'Adding...';
      }

      try {
        if (pendingCartItems.size) {
          setCartDrawerLoading(true);
          const synced = await flushPendingCartUpdates();
          if (!synced) throw new Error('Pending cart update failed');
        } else if (cartSurfaceRefreshPending) {
          cancelCartSurfaceRefresh();
        }

        await enqueueCartMutation(async () => {
          previousCartCount = getDisplayedCartCount();
          const formData = new FormData(form);
          const addedQuantity = Math.max(1, Number(formData.get('quantity') || 1) || 1);

          formData.set('sections', 'cart-drawer');
          formData.set('sections_url', routes.root || '/');
          setCartCount(previousCartCount + addedQuantity);
          openCartDrawer();
          setCartDrawerLoading(true);

          const response = await fetch(cartAddAjaxUrl, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData
          });

          if (!response.ok) throw new Error('Add to cart failed');
          const payload = await response.json();
          if (!payload.sections || !renderCartDrawer(payload.sections['cart-drawer'])) {
            await refreshCartDrawer({ updateCount: false });
          }
          openCartDrawer();
        });
      } catch (error) {
        console.warn('Add to cart failed', error);
        setCartCount(previousCartCount);
        setCartDrawerLoading(false);
        form.submit();
      } finally {
        setCartDrawerLoading(false);
        if (button) {
          button.disabled = false;
          button.textContent = originalText;
        }
      }
    });
  };

  const initProductGallery = () => {
    qsa('[data-product-gallery]').forEach((gallery) => {
      const mainImage = qs('[data-gallery-main-image]', gallery);
      if (!mainImage) return;

      const showThumb = (thumb) => {
        qsa('[data-gallery-thumb]', gallery).forEach((node) => node.setAttribute('aria-current', 'false'));
        thumb.setAttribute('aria-current', 'true');

        mainImage.src = thumb.dataset.mediaSrc;
        if (thumb.dataset.mediaSrcset) mainImage.srcset = thumb.dataset.mediaSrcset;
        mainImage.alt = thumb.dataset.mediaAlt || '';
      };

      gallery.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb) showThumb(thumb);
      });

      gallery.addEventListener('variant:change', (event) => {
        const thumb = qsa('[data-gallery-thumb]', gallery).find((node) => node.dataset.mediaId === event.detail.mediaId);
        if (thumb) showThumb(thumb);
      });
    });
  };

  const initVariantPickers = () => {
    const productForms = qsa('[data-product-form]');
    const stickyAddButton = qs('[data-sticky-add]');
    if (!productForms.length && !stickyAddButton) return;

    productForms.forEach((form) => {
      const select = qs('[data-product-variants]', form);
      if (!select) return;

      const addButton = qs('[data-add-to-cart]', form);
      const priceTargets = qsa('[data-product-price]');
      const stickyPrice = qs('[data-sticky-price]');

      const updateVariant = () => {
        const selectedValues = qsa('[data-option-input]:checked', form).map((input) => input.value);
        const matchingOption = Array.from(select.options).find((option) => {
          const optionValues = [option.dataset.option1, option.dataset.option2, option.dataset.option3].filter(Boolean);
          return optionValues.every((value, index) => value === selectedValues[index]);
        }) || select.selectedOptions[0];

        if (!matchingOption) return;

        select.value = matchingOption.value;
        const gallery = qs('[data-product-gallery]', form.closest('.product-page'));
        if (gallery && matchingOption.dataset.mediaId) {
          gallery.dispatchEvent(new CustomEvent('variant:change', {
            detail: { mediaId: matchingOption.dataset.mediaId }
          }));
        }
        const available = matchingOption.dataset.available === 'true';
        const priceHtml = moneyFromText(matchingOption.dataset.priceHtml);

        if (addButton) {
          addButton.disabled = !available;
          addButton.textContent = available ? theme.strings.addToCart : theme.strings.soldOut;
        }

        priceTargets.forEach((target) => {
          if (priceHtml) target.innerHTML = priceHtml;
        });

        if (stickyPrice && priceHtml) stickyPrice.innerHTML = priceHtml;
      };

      form.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-input]')) updateVariant();
      });

      updateVariant();
    });

    if (stickyAddButton) {
      doc.addEventListener('click', (event) => {
        const stickyButton = event.target.closest('[data-sticky-add]');
        if (!stickyButton) return;
        const form = qs('[data-product-form]');
        if (form) {
          const submit = qs('[data-add-to-cart]', form);
          if (submit) submit.click();
        }
      });
    }
  };

  const initFilters = () => {
    const drawer = qs('[data-filter-drawer]');
    const sortControls = qsa('[data-facet-sort]');
    if (!drawer && !sortControls.length) return;

    if (drawer) {
      doc.addEventListener('click', (event) => {
        const more = event.target.closest('[data-filter-more]');
        if (more) {
          const expanded = more.getAttribute('aria-expanded') !== 'true';
          qsa('[data-filter-extra]', more.closest('.filter-group')).forEach((value) => {
            value.hidden = !expanded;
          });
          more.setAttribute('aria-expanded', String(expanded));
          more.textContent = expanded ? more.dataset.lessText : more.dataset.moreText;
          return;
        }

        const open = event.target.closest('[data-filter-open]');
        const close = event.target.closest('[data-filter-close]');
        if (open) {
          event.preventDefault();
          togglePanel(drawer, true);
        }
        if (close) {
          event.preventDefault();
          togglePanel(drawer, false);
        }
      });
    }

    sortControls.forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });
  };

  const initRecommendations = () => {
    qsa('product-recommendations').forEach(async (element) => {
      const url = element.dataset.url;
      if (!url) return;

      try {
        const response = await fetch(url);
        if (!response.ok) return;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const recommendations = parsed.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim().length) {
          element.innerHTML = recommendations.innerHTML;
        }
      } catch (error) {
        console.warn('Product recommendations failed', error);
      }
    });
  };

  const initRecentlyViewed = () => {
    const source = qs('[data-recently-viewed-current]');
    const target = qs('[data-recently-viewed-grid]');
    const section = qs('[data-recently-viewed-section]');
    if (!source && !target && !section) return;

    const key = 'mahfouz_recently_viewed';
    let current = null;
    let items = [];
    let rendered = 0;

    try {
      items = JSON.parse(localStorage.getItem(key) || '[]');
      current = source ? JSON.parse(source.textContent) : null;
    } catch (error) {
      items = [];
    }

    if (target) {
      items
        .filter((item) => !current || item.handle !== current.handle)
        .slice(0, 4)
        .forEach((item) => {
          const card = doc.createElement('article');
          card.className = 'product-card';
          const itemUrl = escapeAttribute(item.url);
          const itemTitle = escapeAttribute(item.title);
          const image = item.image
            ? `<img src="${escapeAttribute(item.image)}" alt="${itemTitle}" loading="lazy" width="420" height="500">`
            : `<div class="product-card__placeholder">${escapeAttribute(theme.strings.brandName || 'Mahfouz Stores')}</div>`;
          card.innerHTML = `
            <div class="product-card__media">
              <a class="product-card__image-link" href="${itemUrl}">${image}</a>
            </div>
            <div class="product-card__info">
              <h3 class="product-card__title"><a href="${itemUrl}"></a></h3>
              <div class="price">${item.price}</div>
            </div>
          `;
          qs('.product-card__title a', card).textContent = item.title;
          target.appendChild(card);
          rendered += 1;
        });

      if (section && rendered === 0) section.hidden = true;
    }

    if (current) {
      const nextItems = [current, ...items.filter((item) => item.handle !== current.handle)].slice(0, 8);
      localStorage.setItem(key, JSON.stringify(nextItems));
    }
  };

  const initEscape = () => {
    doc.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeCartDrawer();
      const mobileMenu = qs('[data-mobile-menu]');
      const filterDrawer = qs('[data-filter-drawer]');
      togglePanel(mobileMenu, false);
      togglePanel(filterDrawer, false);
      setBodyLock(false);
    });
  };

  doc.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initMobileMenu();
    initCartInteractions();
    initProductGallery();
    initVariantPickers();
    initFilters();
    initRecommendations();
    initRecentlyViewed();
    initEscape();
  });
})();
